/**
 * CommonConditionBar — 작업 칸의 「공통 조건」 줄(설계 FE-20260913 §2 · §3, 오너 결정 D3 「시각적 표출만 잘 되면 됨」).
 *
 *   - 하위 행이 있고 저장 조건이 있는 행 — 「공통 조건 · 하위 N개 행 중 M개에 들어 있음」 · [하위 행에 적용]
 *   - 상위 행에 저장 조건이 있는 행 — 가장 가까운 상위 행의 공통 항과, 그 항이 이 행에 들어 있는지(들어 있음 · 이 행에서 바꿈 · 빠짐).
 *     빠진 항이 있으면 [이 행에도 적용] — 이 행 **초안**에만 넣는다(저장은 [저장], 검색은 0.8초 자동 검색).
 * 한 행이 둘 다일 수 있다(중간 단원). 공통 여부는 상위 행의 **지금 저장 조건**과 똑같은 항인지로만 가른다(commonConditions 규칙 8).
 *
 * 둘 다 **한 줄 카드**다(오너 2026-09-14 「항상 보이게는 하지 말고 한 행짜리 카드로」) — 상태와 버튼은 그 한 줄에 둔다.
 * 상위 공통 항의 칩은 줄을 눌러야 펼친다. 다른 행으로 옮기면 다시 접는다(펼침이 행마다 따라다니면 「항상 보임」과 같아진다).
 */
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../common';
import { describeTerm } from './syllabusQuery';
import { descendantsOf } from './treeIndex';
import {
  applyCommon, commonCoverage, nearestCommonSource, sameTerm, termKey,
} from './commonConditions';
import MathName from './MathName';

// 좌우 8px — 바로 아래 조건 카드(QuickConditions p-2)와 글자 시작점을 맞춘다(오너 2026-09-14)
const boxCls = 'rounded-xl border border-dashed border-primary/40 bg-primary/5 px-2 py-1.5';
// 한 줄 — 긴 행 이름은 줄이 아니라 이름이 줄어든다(버튼·상태는 남는다)
const lineCls = 'flex min-w-0 items-center gap-2 text-[11px] text-gray-500';
const chipCls = (state) => `inline-flex max-w-full items-center gap-1 truncate rounded-full border bg-white px-2 py-0.5 text-[11px] ${
  state === 'present' ? 'border-primary/40 text-gray-700' : 'border-gray-200 text-gray-400'
}${state === 'missing' ? ' line-through' : ''}`;

const groupsOf = (expr) => (Array.isArray(expr?.groups) ? expr.groups : []);
const uniqueTermsOf = (expr) => {
  const seen = new Set();
  return groupsOf(expr).flatMap((g) => (Array.isArray(g?.terms) ? g.terms : [])).filter((t) => {
    const k = termKey(t);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

export default function CommonConditionBar({
  ws, node, id, query, dirty, owner, names, onPull, onOpenApply,
}) {
  // 펼친 행의 id — 다른 행이면 접힌 것으로 본다(부품이 행마다 새로 서지 않는다)
  const [openFor, setOpenFor] = useState(null);
  const serverQueryOf = (k) => ws.index.byId.get(String(k))?.query ?? null;
  const desc = descendantsOf(ws.index, id);
  const isSource = node.query != null && desc.length > 0;
  const source = nearestCommonSource(ws.index, id, serverQueryOf);
  if (!isSource && !source) return null;

  let sourceBox = null;
  if (isSource) {
    const covered = desc.filter((d) => commonCoverage(node.query, serverQueryOf(d)).missing.length === 0).length;
    const left = desc.length - covered;
    sourceBox = (
      <div className={boxCls} data-testid="common-source">
        <div className={lineCls}>
          <p
            className="min-w-0 flex-1 truncate"
            title={`이 행의 조건이 하위 ${desc.length}개 행의 공통 조건입니다${left ? ` — ${left}개 행에는 아직 없음` : ''}`}
          >
            <span className="font-semibold text-gray-600">공통 조건</span> · 하위 {desc.length}개 행 중 {covered}개에 들어 있음
          </p>
          {owner && (
            <Button
              size="xs"
              variant="secondary"
              className="shrink-0"
              onClick={onOpenApply}
              disabled={dirty || ws.pendingWrites > 0}
              title={dirty ? '저장 전 변경이 있습니다 — [저장 · 하위 행에 반영]으로 저장하면서 반영하세요' : undefined}
            >
              하위 행에 적용
            </Button>
          )}
        </div>
      </div>
    );
  }

  let childBox = null;
  if (source) {
    const cov = commonCoverage(source.query, query);
    const rowGroups = groupsOf(query);
    const stateOf = (t) => {
      if (cov.missing.some((m) => sameTerm(m, t))) return 'missing';
      return rowGroups.length && rowGroups.every((g) => (g.terms || []).some((x) => sameTerm(x, t))) ? 'present' : 'overridden';
    };
    const pulled = cov.missing.length ? applyCommon({ current: query, common: source.query }) : null;
    const skipped = pulled?.status === 'skipped';
    const open = openFor === id;
    const terms = uniqueTermsOf(source.query);
    childBox = (
      <div className={boxCls} data-testid="common-inherited">
        <div className={lineCls}>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpenFor(open ? null : id)}
            title={terms.map((t) => describeTerm(t, names)).join(' · ')}
            className="flex min-w-0 flex-1 items-center gap-1 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="shrink-0 font-semibold text-gray-600">공통 조건</span>
            <span className="min-w-0 truncate">· 상위 「<MathName text={source.name} compact />」</span>
            {cov.missing.length > 0 ? (
              <span className="shrink-0 font-medium text-amber-700">
                · 이 행에 없음 {cov.missing.length}개{skipped ? ' · 합칠 수 없음' : ''}
              </span>
            ) : (
              <span className="shrink-0 font-medium text-primary">· 모두 들어 있음</span>
            )}
            <ChevronDown className={`size-3.5 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {pulled?.next && pulled.status !== 'unchanged' && (
            <Button size="xs" variant="secondary" className="shrink-0" onClick={() => onPull(pulled.next)}>이 행에도 적용</Button>
          )}
        </div>
        {open && (
          <div className="mt-1.5 space-y-1">
            <div className="flex flex-wrap gap-1">
              {terms.map((t) => {
                const state = stateOf(t);
                const text = describeTerm(t, names);
                let title = `${text} — 이 행에 들어 있음`;
                if (state === 'missing') title = `${text} — 이 행에 없음`;
                else if (state === 'overridden') title = `${text} — 이 행이 따로 정해 두어 이 행 값을 씀`;
                return (
                  <span key={termKey(t)} className={chipCls(state)} title={title}>
                    {state === 'present' && <span className="text-[9px] font-bold text-primary">공통</span>}
                    <MathName text={text} compact />
                    {state === 'overridden' && <span className="text-[9px]">· 이 행에서 바꿈</span>}
                  </span>
                );
              })}
            </div>
            {skipped && <p className="text-[11px] text-amber-700">합칠 수 없습니다: {pulled.reason?.message || '서로 맞지 않는 조건'}</p>}
            {cov.missing.length === 0 && <p className="text-[11px] text-gray-500">칩은 이 행에서 따로 바꿀 수 있습니다.</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {sourceBox}
      {childBox}
    </>
  );
}
